import React from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Building2, Check, Loader2 } from "lucide-react";
import {
    EMPLOYER_PLANS,
    EMPLOYER_PRICING_GATE,
    EMPLOYER_TRIAL_INTRO,
    EMPLOYER_TRIAL_TERMS_TEMPLATE,
    SUBSCRIPTION_PLACEMENT_COPY,
} from "@/config/employerPricing";

export const EmployerSubscriptionSection = ({
    preview = null,
    onCheckout = null,
    stripeReady = true,
    checkoutBusy = null,
}) => {
    const reducedMotion = useReducedMotion();
    const isPreview = preview?.status === "draft";
    return <>
        <section className="pb-8" data-testid="pricing-employer-section">
            <div className="mx-auto max-w-6xl px-6">
                <div className="mb-6">
                    <div className="overline inline-flex items-center gap-1"><Building2 size={11} /> For Employers</div>
                    <h2 className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground" data-testid="pricing-employer-heading">Software subscriptions</h2>
                    <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
                        Both plans include a <strong className="text-foreground">14-day free trial</strong>. A credit card is required to start — your card is charged on day 15 unless you cancel before then.
                    </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="pricing-employer-tiers">
                    {EMPLOYER_PLANS.map(plan => {
                        const draft = isPreview ? preview.plans.find(p => p.key === plan.key) : null;
                        const id = `pricing-employer-${plan.key}`;
                        const priceLabel = draft?.price_label || plan.priceLabel || EMPLOYER_PRICING_GATE;
                        const period = draft?.period || plan.period || "";
                        const trialDays = draft?.trial_days ?? plan.trialDays ?? 0;
                        const ctaLabel = draft?.cta || plan.cta || "Contact us";
                        const canCheckout = Boolean(onCheckout && plan.planKey && stripeReady);
                        const isBusy = checkoutBusy && checkoutBusy === plan.planKey;
                        const handleClick = (e) => {
                            if (!canCheckout) return;
                            e.preventDefault();
                            onCheckout(plan.planKey);
                        };
                        return <motion.article key={plan.key} data-testid={id}
                            initial={reducedMotion ? false : { opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                            transition={{ duration: 0.35 }}
                            className={`min-w-0 rounded-lg border bg-card p-6 sm:p-8 flex flex-col ${plan.highlight ? "border-emerald-500/60 ring-2 ring-emerald-500/40 shadow-sm" : "border-border"}`}>
                            <div className="flex flex-wrap items-center gap-3 mb-2">
                                <h3 className="font-heading font-semibold text-2xl text-foreground" data-testid={`${id}-name`}>{plan.name}</h3>
                                {plan.highlight && !draft?.badge && (
                                    <span className="rounded-full bg-emerald-500 text-white px-3 py-1 text-[10px] uppercase tracking-wider font-bold" data-testid={`${id}-badge-popular`}>Most popular</span>
                                )}
                                {draft?.badge && <span className="rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-700 dark:text-emerald-400 px-3 py-1 text-xs font-semibold" data-testid={`${id}-badge`}>{draft.badge}</span>}
                            </div>
                            <p className="text-sm text-muted-foreground mb-5" data-testid={`${id}-description`}>{plan.description}</p>
                            <div className="flex items-baseline gap-1 mb-6">
                                <span className="font-heading font-bold text-foreground text-4xl" data-testid={`${id}-price`}>{priceLabel}</span>
                                {period && <span className="text-sm text-muted-foreground">{period}</span>}
                            </div>
                            <ul className="space-y-3 text-sm mb-8 flex-1" data-testid={`${id}-features`}>
                                {plan.features.map((feature, index) => <li key={feature} className="flex items-start gap-2" data-testid={`${id}-feature-${index}`}>
                                    <Check size={15} className="mt-0.5 text-emerald-500 shrink-0" /><span>{feature}</span>
                                </li>)}
                            </ul>
                            <Link
                                to={canCheckout ? "#" : (draft ? `/admin/pricing-preview/checkout?plan=${plan.key}` : "/signup?role=employer")}
                                onClick={handleClick}
                                className={`${plan.highlight ? "btn-green" : "btn-outline"} inline-flex w-full items-center justify-center gap-2 text-sm text-center transition-colors ${canCheckout && !stripeReady ? "opacity-70" : ""}`}
                                data-testid={`${id}-cta`}
                                title={plan.planKey && !stripeReady ? "Stripe is not yet configured for this environment" : undefined}
                            >
                                {isBusy
                                    ? <Loader2 size={14} className="animate-spin" />
                                    : <>{ctaLabel} <ArrowRight size={15} className="shrink-0" /></>}
                            </Link>
                            {trialDays > 0 && (() => {
                                // Interpolate the per-plan sentence with
                                // this plan's displayed price so every
                                // card quotes its own $99 or $249.
                                const planTerms = preview?.trial_terms
                                    || EMPLOYER_TRIAL_TERMS_TEMPLATE.replace(/<PLAN_PRICE>/g, priceLabel);
                                return <p className="mt-3 text-xs leading-relaxed text-muted-foreground" data-testid={`${id}-trial-terms`}>{planTerms}</p>;
                            })()}
                        </motion.article>;
                    })}
                </div>
            </div>
        </section>
        <section className="pb-8" data-testid="pricing-subscription-placement-divider">
            <div className="mx-auto max-w-6xl px-6">
                <p className="border-y border-border py-6 text-sm leading-relaxed text-muted-foreground" data-testid="pricing-subscription-placement-copy">{SUBSCRIPTION_PLACEMENT_COPY}</p>
            </div>
        </section>
    </>;
};
